import { Box, RadioGroup } from "@outschool/backpack";
import { Search as SearchFilters } from "@outschool/business-rules";
import {
  useCurrencyLocalization,
  useLocalPriceDisplay,
  useTranslation
} from "@outschool/localization";
import _ from "lodash";
import React from "react";

import usePriceFilterString from "../../../../hooks/usePriceFilterString";
import { Filter } from "../Filter";
import { ListingChildFilterProps } from "./ListingFilters";

export function ContextualPriceFilter({
  searchFilters,
  onChange,
  selectedSearchToggle = SearchFilters.SearchToggleType.ALL
}: ListingChildFilterProps & {
  selectedSearchToggle: SearchFilters.SearchToggleType;
}) {
  const { t } = useTranslation(
    "client\\components\\search\\mobile\\filters\\PriceFilter"
  );
  const isCourseToggleSelected =
    selectedSearchToggle === SearchFilters.SearchToggleType.COURSES;

  // Display the prices differently depending on which tab is selected
  const {
    getTranslatedPriceStrings,
    getTranslatedPricePerSessionStrings,
    getTranslatedPricePerWeekStrings
  } = usePriceFilterString();

  const priceString =
    selectedSearchToggle === SearchFilters.SearchToggleType.SELF_PACED
      ? getTranslatedPricePerWeekStrings
      : [
          SearchFilters.SearchToggleType.COURSES,
          SearchFilters.SearchToggleType.ONE_TIME
        ].includes(selectedSearchToggle)
      ? getTranslatedPriceStrings
      : getTranslatedPricePerSessionStrings;

  const pricePerMeetingValue = _.pick(
    searchFilters,
    SearchFilters.PRICE_PER_MEETING_FILTERS
  );
  const pricePerCourseValue = _.pick(
    searchFilters,
    SearchFilters.PRICE_PER_COURSE_FILTERS
  );
  const valueKeys = {
    max: isCourseToggleSelected ? "pricePerCourseMax" : "pricePerMeetingMax",
    min: isCourseToggleSelected ? "pricePerCourseMin" : "pricePerMeetingMin"
  };
  const [priceMax, priceMin] = isCourseToggleSelected
    ? [
        pricePerCourseValue.pricePerCourseMax,
        pricePerCourseValue.pricePerCourseMin
      ]
    : [
        pricePerMeetingValue.pricePerMeetingMax,
        pricePerMeetingValue.pricePerMeetingMin
      ];

  const price:
    | Partial<{ priceMax: number | null; priceMin: number | null }>
    | {} = {};
  if (priceMax) {
    // @ts-ignore
    price["priceMax"] = priceMax;
  }
  if (priceMin) {
    // @ts-ignore
    price["priceMin"] = priceMin;
  }
  const { displayPriceMinMax } = useLocalPriceDisplay();
  const { currencyCode } = useCurrencyLocalization();

  const radioOptions = SearchFilters.PRICE_OPTIONS[selectedSearchToggle].map(
    option => {
      return {
        label: displayPriceMinMax(option as any, priceString),
        value: JSON.stringify(option)
      };
    }
  );

  return (
    <Filter
      showTopBorder={false}
      sectionTitle={
        isCourseToggleSelected
          ? t("Price per course ({{currencyCode}})", { currencyCode })
          : t("Price per class ({{currencyCode}})", { currencyCode })
      }
    >
      <Box
        sx={{
          padding: "12px 24px 24px 0px"
        }}
      >
        <RadioGroup
          name="price"
          options={radioOptions}
          value={JSON.stringify(price)}
          onChange={(_e: any, v: string) => {
            const { priceMax, priceMin } = JSON.parse(v);
            const parsedValue = priceMax
              ? { [valueKeys.max]: priceMax }
              : priceMin
              ? { [valueKeys.min]: priceMin }
              : {};
            onChange?.(parsedValue);
          }}
        />
      </Box>
    </Filter>
  );
}
